/**
 * table-wrapper.js - A library for automatically wrapping tables with a scrollable figure element
 * and applying visual indicators (shadows) to indicate horizontal and vertical scroll overflow.
 *
 * By default, horizontal scrolling is enabled, and optional attributes allow for enabling vertical
 * scrolling, setting maximum table heights, and customizing scroll note text and shadows.
 *
 * @class TableWrapper
 * @author Nick Tesh
 * @version 1.0.0
 * @param {Object} [options={}] - Configuration options to customize the library behavior.
 * @property {string} parentClass - Class for the parent element wrapping the table.
 * @property {string} figureClass - Class for the scrollable figure element wrapping the table.
 * @property {string} shadowLeftClass - Class for the left shadow indicator.
 * @property {string} shadowRightClass - Class for the right shadow indicator.
 * @property {string} shadowTopClass - Class for the top shadow indicator.
 * @property {string} shadowBottomClass - Class for the bottom shadow indicator.
 * @property {string} defaultAriaLabel - Default aria-label for the figure element for accessibility.
 * @property {string} scrollNoteClass - Class for the scroll note displayed above the parent element.
 */
class TableWrapper {
  /**
   * @constructor
   * @param {Object} [options={}] - Custom configuration options.
   */
  constructor(options = {}) {
    this.defaultOptions = {
      parentClass: 'tw-parent',
      figureClass: 'table-figure',
      shadowLeftClass: 'left-shadow-block',
      shadowRightClass: 'right-shadow-block',
      shadowTopClass: 'top-shadow-block',
      shadowBottomClass: 'bottom-shadow-block',
      defaultAriaLabel: 'Scrollable table',
      scrollNoteClass: 'tw-scroll-note',
    };
    this.options = { ...this.defaultOptions, ...options };
  }

  /**
   * Initializes the library by wrapping tables, updating shadows, and attaching event listeners.
   * @public
   */
  init() {
    this.wrapTables();
    this.updateShadows();
    this.attachListeners();
  }

  /**
   * Wraps tables in a scrollable figure element, applies max height, and adds shadows and scroll notes.
   * @private
   */
  wrapTables() {
    const parents = document.querySelectorAll(`.${this.options.parentClass}`);
    let uniqueIdCounter = 0;

    parents.forEach((parent) => {
      const content = parent.querySelector('table');
      if (!content) return;

      // Assign unique IDs
      if (!parent.id) {
        parent.id = `tw-parent-${uniqueIdCounter++}`;
      }

      // Check if a scroll note already exists and skip adding another
      const existingScrollNote = parent.previousElementSibling;
      if (
        existingScrollNote?.classList.contains(this.options.scrollNoteClass)
      ) {
        return;
      }

      // Wrap the table inside a figure
      const figure = document.createElement('figure');
      figure.className = this.options.figureClass;
      figure.setAttribute('tabindex', '0');
      figure.setAttribute(
        'aria-label',
        parent.dataset.ariaLabel || this.options.defaultAriaLabel,
      );

      content.parentNode.insertBefore(figure, content);
      figure.appendChild(content);

      // Add max-height for vertical scrolling if specified
      const maxHeight = parent.dataset.maxHeight;
      const mobileWidth = parent.dataset.mobileWidth;
      if (maxHeight) {
        if (!mobileWidth || window.innerWidth <= parseInt(mobileWidth, 10)) {
          figure.style.maxHeight = `${maxHeight}px`;
          figure.style.overflowY = 'auto';
        }
      }

      // Add horizontal shadows
      parent.insertAdjacentHTML(
        'afterbegin',
        `<span class="${this.options.shadowLeftClass}" aria-hidden="true"></span>`,
      );
      parent.insertAdjacentHTML(
        'beforeend',
        `<span class="${this.options.shadowRightClass}" aria-hidden="true"></span>`,
      );

      // Add vertical shadows
      if (maxHeight) {
        parent.insertAdjacentHTML(
          'afterbegin',
          `<span class="${this.options.shadowTopClass}" aria-hidden="true"></span>`,
        );
        parent.insertAdjacentHTML(
          'beforeend',
          `<span class="${this.options.shadowBottomClass}" aria-hidden="true"></span>`,
        );
      }

      // Add dynamic scroll note above the parent
      const scrollNote = document.createElement('div');
      scrollNote.className = this.options.scrollNoteClass;
      scrollNote.textContent = 'Scroll to view ↔';
      parent.parentNode.insertBefore(scrollNote, parent);
    });
  }

  /**
   * Updates the visibility of shadows based on scroll position and overflow.
   * @private
   */
  updateShadows() {
    const figures = document.querySelectorAll(`.${this.options.figureClass}`);

    figures.forEach((figure) => {
      const parent = figure.closest(`.${this.options.parentClass}`);
      const content = figure.querySelector('table');
      const scrollNote = parent.previousElementSibling;

      if (!content) return;

      // Horizontal Shadows
      const scrollLeft = figure.scrollLeft;
      const maxScrollLeft = content.scrollWidth - figure.clientWidth;

      const shadowLeft = parent.querySelector(
        `.${this.options.shadowLeftClass}`,
      );
      const shadowRight = parent.querySelector(
        `.${this.options.shadowRightClass}`,
      );

      let hasHorizontalScroll = false;
      if (content.scrollWidth > figure.clientWidth) {
        hasHorizontalScroll = true;
        shadowLeft?.classList.toggle('visible', scrollLeft > 0);
        shadowRight?.classList.toggle('visible', scrollLeft < maxScrollLeft);
      } else {
        shadowLeft?.classList.remove('visible');
        shadowRight?.classList.remove('visible');
      }

      // Vertical Shadows
      let hasVerticalScroll = false;
      if (figure.style.maxHeight) {
        const scrollTop = figure.scrollTop;
        const maxScrollTop = content.scrollHeight - figure.clientHeight;

        const shadowTop = parent.querySelector(
          `.${this.options.shadowTopClass}`,
        );
        const shadowBottom = parent.querySelector(
          `.${this.options.shadowBottomClass}`,
        );

        if (content.scrollHeight > figure.clientHeight) {
          hasVerticalScroll = true;
          shadowTop?.classList.toggle('visible', scrollTop > 0);
          shadowBottom?.classList.toggle('visible', scrollTop < maxScrollTop);
        } else {
          shadowTop?.classList.remove('visible');
          shadowBottom?.classList.remove('visible');
        }
      }

      // Update scroll note based on scroll direction
      // This dynamically updates the "Scroll to view" note to include visual and screen reader indicators
      // based on the scrollable directions detected for the table:
      // - Horizontal scroll: Adds a "left or right" indication with a corresponding SVG icon.
      // - Vertical scroll: Adds an "up or down" indication with a corresponding SVG icon.
      // - Both horizontal and vertical scrolls: Combines both indications.
      // If no scrolling is detected, the note is hidden.
      if (scrollNote) {
        if (hasHorizontalScroll && hasVerticalScroll) {
          scrollNote.innerHTML = `
          <span>Scroll to view <span class="sr-only">up, down, left, or right</span></span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M19.2307 10.6154H0.769228C0.344384 10.6154 0 10.271 0 9.84615C0 9.4213 0.344384 9.07692 0.769228 9.07692H19.2307C19.6556 9.07692 19.9999 9.4213 19.9999 9.84615C19.9999 10.271 19.6556 10.6154 19.2307 10.6154Z"/><path d="M3.84615 13.6923C3.64927 13.6923 3.45242 13.6172 3.30223 13.467L0.225317 10.3901C-0.0751056 10.0897 -0.0751056 9.60265 0.225317 9.30223L3.30223 6.22532C3.60265 5.92489 4.08969 5.92489 4.39007 6.22532C4.69046 6.52574 4.6905 7.01278 4.39007 7.31316L1.85708 9.84615L4.39007 12.3791C4.6905 12.6795 4.6905 13.1666 4.39007 13.467C4.23988 13.6172 4.04304 13.6923 3.84615 13.6923Z"/><path d="M16.1538 13.6923C15.957 13.6923 15.7601 13.6172 15.6098 13.467C15.3094 13.1666 15.3094 12.6795 15.6098 12.3791L18.1429 9.84615L15.6099 7.31316C15.3095 7.01274 15.3095 6.5257 15.6099 6.22532C15.9103 5.92493 16.3974 5.9249 16.6978 6.22532L19.7747 9.30223C20.0751 9.60265 20.0751 10.0897 19.7747 10.3901L16.6978 13.467C16.5475 13.6172 16.3506 13.6923 16.1538 13.6923Z"/><path d="M9.38464 19.2307V0.769228C9.38464 0.344384 9.72902 0 10.1539 0C10.5787 0 10.9231 0.344384 10.9231 0.769228V19.2307C10.9231 19.6556 10.5787 19.9999 10.1539 19.9999C9.72902 19.9999 9.38464 19.6556 9.38464 19.2307Z"/><path d="M6.30771 3.84615C6.30771 3.64927 6.38278 3.45242 6.53297 3.30223L9.60989 0.225317C9.91031 -0.0751056 10.3973 -0.0751056 10.6978 0.225317L13.7747 3.30223C14.0751 3.60265 14.0751 4.08969 13.7747 4.39007C13.4743 4.69046 12.9872 4.6905 12.6868 4.39007L10.1538 1.85708L7.62089 4.39007C7.32047 4.6905 6.8334 4.6905 6.53301 4.39007C6.38278 4.23988 6.30771 4.04304 6.30771 3.84615Z"/><path d="M6.30769 16.1538C6.30769 15.957 6.38277 15.7601 6.53296 15.6098C6.83338 15.3094 7.32046 15.3094 7.62084 15.6098L10.1538 18.1429L12.6868 15.6099C12.9872 15.3095 13.4743 15.3095 13.7747 15.6099C14.0751 15.9103 14.0751 16.3974 13.7747 16.6978L10.6978 19.7747C10.3973 20.0751 9.91029 20.0751 9.60987 19.7747L6.53296 16.6978C6.38277 16.5475 6.30769 16.3506 6.30769 16.1538Z"/></svg>`;
        } else if (hasHorizontalScroll) {
          scrollNote.innerHTML = `
          <span>Scroll to view <span class="sr-only">left or right</span></span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M19.2307 10.6154H0.769228C0.344384 10.6154 0 10.271 0 9.84615C0 9.4213 0.344384 9.07692 0.769228 9.07692H19.2307C19.6556 9.07692 19.9999 9.4213 19.9999 9.84615C19.9999 10.271 19.6556 10.6154 19.2307 10.6154Z"/><path d="M3.84615 13.6923C3.64927 13.6923 3.45242 13.6172 3.30223 13.467L0.225317 10.3901C-0.0751056 10.0897 -0.0751056 9.60265 0.225317 9.30223L3.30223 6.22532C3.60265 5.92489 4.08969 5.92489 4.39007 6.22532C4.69046 6.52574 4.6905 7.01278 4.39007 7.31316L1.85708 9.84615L4.39007 12.3791C4.6905 12.6795 4.6905 13.1666 4.39007 13.467C4.23988 13.6172 4.04304 13.6923 3.84615 13.6923Z"/><path d="M16.1538 13.6923C15.957 13.6923 15.7601 13.6172 15.6098 13.467C15.3094 13.1666 15.3094 12.6795 15.6098 12.3791L18.1429 9.84615L15.6099 7.31316C15.3095 7.01274 15.3095 6.5257 15.6099 6.22532C15.9103 5.92493 16.3974 5.9249 16.6978 6.22532L19.7747 9.30223C20.0751 9.60265 20.0751 10.0897 19.7747 10.3901L16.6978 13.467C16.5475 13.6172 16.3506 13.6923 16.1538 13.6923Z"/></svg>`;
        } else if (hasVerticalScroll) {
          scrollNote.innerHTML = `
          <span>Scroll to view <span class="sr-only">up or down</span></span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M9.84617 20C9.42132 20 9.07693 19.6556 9.07693 19.2308V0.769231C9.07693 0.344385 9.42132 0 9.84617 0C10.271 0 10.6154 0.344385 10.6154 0.769231V19.2308C10.6154 19.6556 10.271 20 9.84617 20Z"/><path d="M9.84616 20C9.64928 20 9.45243 19.9249 9.30224 19.7747L6.22532 16.6978C5.92489 16.3974 5.92489 15.9103 6.22532 15.6099C6.52574 15.3095 7.01278 15.3095 7.31316 15.6099L9.84616 18.1429L12.3791 15.6099C12.6795 15.3095 13.1666 15.3095 13.467 15.6099C13.7674 15.9103 13.7674 16.3974 13.467 16.6978L10.3901 19.7747C10.2399 19.9249 10.043 20 9.84616 20Z" /><path d="M12.9232 4.61543C12.7263 4.61543 12.5295 4.54031 12.3792 4.39012L9.84627 1.85712L7.31327 4.39012C7.01285 4.69054 6.52581 4.69054 6.22542 4.39012C5.92504 4.08969 5.925 3.60266 6.22542 3.30227L9.30235 0.225349C9.60277 -0.075036 10.0898 -0.0750744 10.3902 0.225349L13.4672 3.30227C13.7676 3.60269 13.7676 4.08973 13.4672 4.39012C13.3169 4.54031 13.12 4.61543 12.9232 4.61543Z"/></svg`;
        } else {
          scrollNote.style.display = 'none';
        }
        scrollNote.style.display =
          hasHorizontalScroll || hasVerticalScroll ? 'block' : 'none';
      }
    });
  }

  /**
   * Attaches event listeners for scroll and resize events.
   * @private
   */
  attachListeners() {
    // Attach scroll listeners to each figure
    const figures = document.querySelectorAll(`.${this.options.figureClass}`);
    figures.forEach((figure) => {
      figure.addEventListener('scroll', () => this.updateShadows());
    });

    // Attach resize listener to handle mobile-width logic dynamically
    window.addEventListener('resize', () => {
      this.handleResponsiveMaxHeight();
      this.updateShadows();
    });
  }

  /**
   * Handles the application of max-height and vertical scrolling based on the specified screen width.
   *
   * This method dynamically applies or removes the `max-height` and enables or disables vertical scrolling
   * on the figure element based on the `data-max-height` and `data-mobile-width` attributes. If no
   * `data-mobile-width` is specified, the `max-height` is always applied. Additionally, this method ensures
   * that top and bottom shadows are shown or hidden based on the scrolling requirements.
   *
   * @private
   */
  handleResponsiveMaxHeight() {
    const parents = document.querySelectorAll(`.${this.options.parentClass}`);
    parents.forEach((parent) => {
      const figure = parent.querySelector(`.${this.options.figureClass}`);
      if (!figure) return;

      const maxHeight = parent.dataset.maxHeight;
      const mobileWidth = parent.dataset.mobileWidth;

      if (maxHeight) {
        if (!mobileWidth || window.innerWidth <= parseInt(mobileWidth, 10)) {
          // Apply max-height and enable vertical scrolling
          figure.style.maxHeight = `${maxHeight}px`;
          figure.style.overflowY = 'auto';

          // Ensure top/bottom shadows are available for vertical scrolling
          const shadowTop = parent.querySelector(
            `.${this.options.shadowTopClass}`,
          );
          const shadowBottom = parent.querySelector(
            `.${this.options.shadowBottomClass}`,
          );
          shadowTop?.classList.add('visible');
          shadowBottom?.classList.add('visible');
        } else {
          // Clear max-height and remove vertical shadows
          figure.style.maxHeight = '';
          figure.style.overflowY = '';

          const shadowTop = parent.querySelector(
            `.${this.options.shadowTopClass}`,
          );
          const shadowBottom = parent.querySelector(
            `.${this.options.shadowBottomClass}`,
          );
          shadowTop?.classList.remove('visible');
          shadowBottom?.classList.remove('visible');
        }
      }
    });
  }
}

// Initialize TableWrapper
document.addEventListener('DOMContentLoaded', () => {
  const tableWrapper = new TableWrapper();
  tableWrapper.init();
});